import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Loader2,
  Send,
  Inbox as InboxIcon,
  ArrowLeft,
  ShieldCheck,
  Archive,
  ArchiveRestore,
  Trash2,
  ChevronDown,
  Ban,
  Flag,
  MessageSquare,
  MessagesSquare,
  ArrowUpDown,
  ArrowDown,
} from "lucide-react";
import {
  getInbox,
  getThread,
  replyInThread,
  deleteMessage,
  setThreadArchived,
  deleteThread,
} from "@/lib/messages.functions";
import { adImageSrc } from "@/lib/image-src";
import { ReportMessage } from "@/components/ReportMessage";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { inboxQuery } from "@/lib/queries";
import { SystemNotices } from "@/components/SystemNotices";
import { getMyNotifications } from "@/lib/system-notifications.functions";
import { AdminUserTooltip } from "@/components/AdminUserTooltip";
import { formatDateTime, timeAgo } from "@/lib/format-date";
import { ThreadOffers } from "@/components/offers/ThreadOffers";
import {
  SYSTEM_COLOR,
  SYSTEM_ICON,
  parseSystemMessage,
  plainMessagePreview,
} from "@/lib/offer-status";


export const Route = createFileRoute("/_authenticated/meddelanden")({
  head: () => ({
    meta: [
      { title: "Inkorg — Sellavo" },
      { name: "description", content: "Läs och svara på meddelanden om dina annonser på Sellavo." },
      { property: "og:title", content: "Inkorg — Sellavo" },
      {
        property: "og:description",
        content: "Läs och svara på meddelanden om dina annonser på Sellavo.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { annons?: string; med?: string } => ({
    ...(typeof search.annons === "string" ? { annons: search.annons } : {}),
    ...(typeof search.med === "string" ? { med: search.med } : {}),
  }),

  loader: ({ context }) => context.queryClient.ensureQueryData(inboxQuery()),

  component: Inbox,
});

function formatDate(iso: string) {
  return formatDateTime(iso);
}

/** Kort tid i listan: klockslag i dag, annars datum – full tid i title. */
function shortStamp(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const sameDay =
    d.toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" }) ===
    today.toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" });
  return d.toLocaleString("sv-SE", {
    ...(sameDay ? {} : { day: "numeric", month: "short" }),
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Stockholm",
  });
}

type Active = { adId: string; otherUserId: string; title: string; slug: string };

function Inbox() {
  const archiveFn = useServerFn(setThreadArchived);
  const deleteThreadFn = useServerFn(deleteThread);
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const [active, setActive] = useState<Active | null>(null);
  const threadPaneRef = useRef<HTMLDivElement>(null);

  const [showArchived, setShowArchived] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Active | null>(null);
  const [filter, setFilter] = useState<"all" | "sent" | "received">("all");
  const [sort, setSort] = useState<"latest" | "unread" | "received" | "sent">("latest");

  const { data, isLoading } = useQuery(inboxQuery());
  const threads = data ?? [];
  const noticeList = useServerFn(getMyNotifications);
  const { data: notices } = useQuery({
    queryKey: ["system-notifications"],
    queryFn: () => noticeList({}),
    staleTime: 30_000,
  });
  const unreadTotal =
    threads.reduce((sum, t) => sum + (t.unread || 0), 0) +
    (notices ?? []).filter((n) => !n.read_at).length;

  const counts = useMemo(
    () => ({
      all: threads.length,
      sent: threads.filter((t) => t.role === "buyer").length,
      received: threads.filter((t) => t.role === "seller").length,
    }),
    [threads],
  );

  const { open, archived } = useMemo(() => {
    const visible = threads.filter((t) =>
      filter === "all" ? true : filter === "sent" ? t.role === "buyer" : t.role === "seller",
    );
    const byLatest = (a: (typeof threads)[number], b: (typeof threads)[number]) =>
      new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime();
    const sorted = [...visible].sort((a, b) => {
      if (sort === "unread") {
        const d = (b.unread > 0 ? 1 : 0) - (a.unread > 0 ? 1 : 0);
        if (d !== 0) return d;
      } else if (sort === "received") {
        const d = (b.role === "seller" ? 1 : 0) - (a.role === "seller" ? 1 : 0);
        if (d !== 0) return d;
      } else if (sort === "sent") {
        const d = (b.role === "buyer" ? 1 : 0) - (a.role === "buyer" ? 1 : 0);
        if (d !== 0) return d;
      }
      return byLatest(a, b);
    });
    return {
      open: sorted.filter((t) => !t.archived),
      archived: sorted.filter((t) => t.archived),
    };
  }, [threads, filter, sort]);

  // Klick i "Nytt från Sellavo" länkar direkt till rätt konversation – öppna den
  // och scrolla ner så att man ser tråden på en gång, även i mobilen.
  const openedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!search.annons || !search.med || !threads.length) return;
    const key = `${search.annons}:${search.med}`;
    if (openedRef.current === key) return;
    const t = threads.find((x) => x.adId === search.annons && x.otherUserId === search.med);
    if (!t) return;
    openedRef.current = key;
    setActive({ adId: t.adId, otherUserId: t.otherUserId, title: t.title, slug: t.slug });
    requestAnimationFrame(() => {
      threadPaneRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [search.annons, search.med, threads]);


  const archive = useMutation({
    mutationFn: (v: { adId: string; otherUserId: string; archived: boolean }) =>
      archiveFn({ data: v }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["inbox"] }),
  });

  const removeThread = useMutation({
    mutationFn: (v: { adId: string; otherUserId: string }) => deleteThreadFn({ data: v }),
    onSuccess: (_r, v) => {
      setPendingDelete(null);
      if (active?.adId === v.adId && active?.otherUserId === v.otherUserId) setActive(null);
      queryClient.invalidateQueries({ queryKey: ["inbox"] });
    },
  });

  const renderThread = (t: (typeof threads)[number]) => {
    const isActive = active?.adId === t.adId && active?.otherUserId === t.otherUserId;
    const nameLabel = t.otherPreviousName
      ? `${t.otherName} (hette ${t.otherPreviousName})`
      : t.otherName;
    const roleLabel = t.role === "buyer" ? `Du kontaktade ${nameLabel}` : `Din annons · ${nameLabel}`;
    return (
      <li key={`${t.adId}:${t.otherUserId}`} className="relative">
        <button
          type="button"
          onClick={() =>
            setActive({ adId: t.adId, otherUserId: t.otherUserId, title: t.title, slug: t.slug })
          }
          className={`group flex w-full items-start gap-3.5 p-4 pr-14 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
            isActive
              ? "bg-primary/5 hover:bg-primary/[0.07]"
              : "hover:bg-secondary/60"
          }`}
        >
          <span className="relative shrink-0">
            {t.imageUrl ? (
              <img
                src={adImageSrc(t.imageUrl)}
                alt=""
                className="h-14 w-14 rounded-lg object-cover ring-1 ring-border"
              />
            ) : (
              <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-secondary text-muted-foreground">
                <InboxIcon className="h-6 w-6" />
              </span>
            )}
            <span
              title={t.role === "buyer" ? "Du kontaktade säljaren" : "Någon kontaktade dig om din annons"}
              className={`absolute -bottom-1 -left-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-card ${
                t.role === "buyer"
                  ? "bg-primary text-primary-foreground"
                  : "bg-accent text-accent-foreground"
              }`}
            >
              {t.role === "buyer" ? (
                <Send className="h-2.5 w-2.5" />
              ) : (
                <MessageSquare className="h-2.5 w-2.5" />
              )}
            </span>
            {t.unread > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-card bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                {t.unread > 9 ? "9+" : t.unread}
              </span>
            )}
          </span>

          <span className="min-w-0 flex-1">
            <span className="flex items-center justify-between gap-2">
              <span className="truncate font-heading font-semibold text-foreground">{t.title}</span>
              <time
                dateTime={t.lastAt}
                title={`${formatDateTime(t.lastAt)} (${timeAgo(t.lastAt)})`}
                className="shrink-0 text-xs tabular-nums text-muted-foreground"
              >
                {shortStamp(t.lastAt)}
              </time>
            </span>
            <span className="mt-0.5 flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              <span className="truncate">{roleLabel}</span>
              {t.otherIsDemo && (
                <span className="shrink-0 rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  demo
                </span>
              )}
            </span>
            <span className="mt-1.5 block truncate text-sm text-foreground/75">
              {t.lastMine && <span className="font-medium text-primary">Du: </span>}
              {plainMessagePreview(t.lastBody)}
            </span>
          </span>
        </button>
        <div className="absolute right-2 top-2.5 flex flex-col items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <button
            type="button"
            aria-label={t.archived ? `Återställ ${t.title} från arkivet` : `Arkivera ${t.title}`}
            title={t.archived ? "Återställ från arkiv" : "Arkivera konversationen"}
            onClick={() =>
              archive.mutate({ adId: t.adId, otherUserId: t.otherUserId, archived: !t.archived })
            }
            className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          </button>
          {t.archived && (
            <button
              type="button"
              aria-label={`Ta bort hela konversationen om ${t.title}`}
              title="Ta bort hela konversationen"
              onClick={() =>
                setPendingDelete({
                  adId: t.adId,
                  otherUserId: t.otherUserId,
                  title: t.title,
                  slug: t.slug,
                })
              }
              className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </li>
    );
  };

  return (
    <main className="mx-auto w-full max-w-5xl px-4 pb-16 pt-6 sm:px-6 sm:pb-20 sm:pt-10 lg:px-8">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">
        Inkorg
      </h1>
      <p className="mt-2 text-foreground/75">
        All kontakt mellan köpare och säljare sker här. Inga e-postadresser eller telefonnummer
        visas.
      </p>

      {unreadTotal > 0 && (
        <p className="mt-4 inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm font-medium text-foreground">
          <ArrowDown className="h-4 w-4 shrink-0 text-primary" />
          Du har {unreadTotal} oläst{unreadTotal === 1 ? "" : "a"} här nedanför. Öppna
          konversationen så försvinner siffran i sidhuvudet.
        </p>
      )}

      <div className="mt-6 grid min-w-0 gap-6 lg:grid-cols-[340px_minmax(0,1fr)]">
        <div
          className={`min-w-0 overflow-hidden rounded-lg border border-border bg-card ${active ? "hidden lg:block" : "block"}`}
        >
          {threads.length > 0 && (
            <div className="border-b border-border p-2">
              <div className="grid grid-cols-3 gap-0.5 rounded-lg bg-muted p-0.5 sm:gap-1 sm:p-1">
                {(
                  [
                    ["all", "Alla", InboxIcon],
                    ["received", "Mottagna", MessageSquare],
                    ["sent", "Skickade", Send],
                  ] as const
                ).map(([key, label, Icon]) => {
                  const active = filter === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFilter(key)}
                      aria-pressed={active}
                      className={`flex min-w-0 items-center justify-center gap-1 rounded-md px-1 py-1.5 text-[11px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-xs ${
                        active
                          ? "bg-card text-foreground shadow-sm ring-1 ring-border"
                          : "text-foreground/70 hover:text-foreground"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                       <span className="truncate">{label}</span>
                      {counts[key] > 0 && (
                        <span
                          className={`shrink-0 text-[10px] font-bold tabular-nums ${
                            active ? "text-primary" : "text-muted-foreground"
                          }`}
                        >
                          {counts[key]}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 flex items-center justify-end gap-2 px-1">
                <span className="text-[11px] font-medium text-muted-foreground">Sortera</span>
                <Select value={sort} onValueChange={(v) => setSort(v as typeof sort)}>
                  <SelectTrigger className="h-7 w-auto min-w-[10.5rem] gap-2 rounded-full border-border bg-transparent px-3 py-1 text-xs font-medium shadow-none focus:ring-1 focus:ring-ring">
                    <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent align="end" className="min-w-[10.5rem]">
                    {(
                      [
                        ["latest", "Senast kontaktad"],
                        ["unread", "Olästa först"],
                        ["received", "Mina annonser först"],
                        ["sent", "Mina utskick först"],
                      ] as const
                    ).map(([key, label]) => (
                      <SelectItem key={key} value={key} className="text-xs">
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Laddar konversationer…
            </div>
          ) : threads.length === 0 ? (
            <div className="flex flex-col items-center p-8 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
                <InboxIcon className="h-6 w-6 text-muted-foreground" />
              </span>
              <p className="mt-4 font-medium text-foreground">Du har inga meddelanden ännu</p>
              <p className="mt-1 max-w-[16rem] text-sm text-foreground/70">
                När någon hör av sig om en annons dyker konversationen upp här.
              </p>
            </div>
          ) : (
            <>
              {open.length > 0 ? (
                <ul className="divide-y divide-border">{open.map(renderThread)}</ul>
              ) : (
                <div className="flex flex-col items-center p-8 text-center">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
                    <InboxIcon className="h-5 w-5 text-muted-foreground" />
                  </span>
                  <p className="mt-3 text-sm font-medium text-foreground">
                    {filter === "sent"
                      ? "Du har inte skrivit till någon ännu"
                      : filter === "received"
                        ? "Ingen har hört av sig om dina annonser ännu"
                        : "Inga öppna konversationer"}
                  </p>
                  <p className="mt-1 max-w-[16rem] text-xs text-foreground/70">
                    {filter === "sent"
                      ? "Hittar du något fint? Skicka ett meddelande till säljaren."
                      : filter === "received"
                        ? "Dina aktiva annonser visas här när intresserade köpare hör av sig."
                        : "Alla dina konversationer ligger just nu i arkivet."}
                  </p>
                </div>
              )}

              {archived.length > 0 && (
                <div className="border-t border-border bg-muted/30">
                  <button
                    type="button"
                    onClick={() => setShowArchived((v) => !v)}
                    aria-expanded={showArchived}
                    className="flex w-full items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Archive className="h-4 w-4 text-muted-foreground" />
                      Arkiv
                      <span className="rounded-full bg-secondary px-1.5 py-px text-[10px] text-secondary-foreground">
                        {archived.length}
                      </span>
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${showArchived ? "rotate-180" : ""}`}
                    />
                  </button>
                  {showArchived && (
                    <ul className="divide-y divide-border border-t border-border">
                      {archived.map(renderThread)}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div
          ref={threadPaneRef}
          className={`min-w-0 scroll-mt-24 overflow-hidden rounded-lg border border-border bg-card ${active ? "block" : "hidden lg:block"}`}
        >

          {active ? (
            <Thread
              key={`${active.adId}:${active.otherUserId}`}
              {...active}
              onBack={() => setActive(null)}
            />
          ) : (
            <p className="p-8 text-center text-foreground/75">Välj en konversation till vänster.</p>
          )}
        </div>
      </div>

      <div className="mt-8">
        <SystemNotices />
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort hela konversationen?</AlertDialogTitle>
            <AlertDialogDescription>
              Konversationen om „{pendingDelete?.title}” försvinner helt från din inkorg och ditt
              arkiv. Du kan inte ångra det själv. Innehållet raderas inte permanent utan sparas i
              vårt system, så att vi kan ta fram det vid till exempel en utredning eller anmälan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeThread.isPending}>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={removeThread.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (pendingDelete)
                  removeThread.mutate({
                    adId: pendingDelete.adId,
                    otherUserId: pendingDelete.otherUserId,
                  });
              }}
            >
              {removeThread.isPending ? "Tar bort…" : "Ta bort konversationen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}

function Thread({ adId, otherUserId, title, slug, onBack }: Active & { onBack: () => void }) {
  const fetchThread = useServerFn(getThread);
  const reply = useServerFn(replyInThread);
  const removeMessage = useServerFn(deleteMessage);
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [pendingReport, setPendingReport] = useState<{
    id: string;
    body: string;
    reported: boolean;
  } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["thread", adId, otherUserId],
    queryFn: () => fetchThread({ data: { adId, otherUserId } }),
    refetchInterval: 6_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });


  // När tråden öppnas markerar servern meddelandena som lästa – uppdatera
  // därför både listan och siffran i sidhuvudet direkt.
  const clearedRef = useRef(false);
  useEffect(() => {
    if (!data || clearedRef.current) return;
    clearedRef.current = true;
    void queryClient.invalidateQueries({ queryKey: ["unread"] });
    void queryClient.invalidateQueries({ queryKey: ["inbox"] });
  }, [data, queryClient]);

  useEffect(() => {
    if (data && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [data?.messages.length, data?.messages[data?.messages.length - 1]?.id]);

  const remaining = data?.remaining ?? 5;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["thread", adId, otherUserId] });
    queryClient.invalidateQueries({ queryKey: ["inbox"] });
    queryClient.invalidateQueries({ queryKey: ["unread"] });
  };

  const send = useMutation({
    mutationFn: (text: string) => reply({ data: { adId, otherUserId, body: text } }),
    onSuccess: () => {
      setBody("");
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (messageId: string) => removeMessage({ data: { messageId } }),
    onSuccess: () => {
      setPendingDelete(null);
      invalidate();
    },
  });

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-w-0 items-center gap-3 border-b border-border p-4">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onBack}
          aria-label="Tillbaka"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="truncate font-heading font-semibold text-foreground">{title}</p>
          <p className="truncate text-xs text-foreground/75">
            Konversation med{" "}
            <AdminUserTooltip userId={otherUserId}>
              <span className="font-semibold text-primary">{data?.otherName ?? "…"}</span>
            </AdminUserTooltip>
            {data?.otherIsDemo && <span className="ml-1 text-muted-foreground">(demo)</span>}
          </p>
          {data?.otherPreviousName && (
            <p className="truncate text-xs text-amber-600 dark:text-amber-400">
              Bytte nyligen visningsnamn – hette tidigare {data.otherPreviousName}
            </p>
          )}

          {slug && (
            <Link
              to="/a/$slug"
              params={{ slug }}
              className="text-xs font-medium text-primary hover:underline"
            >
              Visa annonsen
            </Link>
          )}
        </div>
      </div>

      <ThreadOffers adId={adId} otherUserId={otherUserId} onChanged={invalidate} />

      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
        {isLoading ? (
          <p className="text-foreground/75">Laddar…</p>
        ) : (
          data?.messages.map((m) => {
            // Bud-händelser visas som neutrala systemrader, inte som chattbubblor
            // från en person – de kommer från Sellavo, inte från motparten.
            const sys = m.deleted ? null : parseSystemMessage(m.body);
            if (sys) {
              const Icon = SYSTEM_ICON[sys.kind];
              return (
                <div key={m.id} className="flex justify-center">
                  <div className="flex min-w-0 max-w-full items-start gap-2 rounded-lg border border-border bg-muted/60 px-3 py-2 text-xs text-muted-foreground sm:max-w-[92%]">
                    <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${SYSTEM_COLOR[sys.kind]}`} />
                    <span className="min-w-0 text-left">
                      <span className="whitespace-pre-wrap break-words text-foreground/80">
                        {sys.text}
                      </span>
                      <span className="ml-1.5 text-[11px] text-muted-foreground">
                        {formatDate(m.created_at)}
                      </span>
                    </span>
                  </div>
                </div>
              );
            }
            return (
              <div
                key={m.id}
                className={`group flex flex-col gap-0.5 ${m.mine ? "items-end" : "items-start"}`}
              >

                <div
                  className={`flex w-full items-center gap-2 ${m.mine ? "justify-end" : "justify-start"}`}
                >
                  {m.mine && !m.deleted && (
                    <button
                      type="button"
                      onClick={() => setPendingDelete(m.id)}
                      aria-label="Ta bort meddelandet"
                      title="Ta bort meddelandet"
                      className="rounded-md p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-secondary hover:text-destructive focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  <div
                    className={`min-w-0 max-w-[80%] overflow-hidden break-words rounded-md px-3 py-2 text-sm ${
                      m.deleted
                        ? "border border-dashed border-border bg-muted text-muted-foreground"
                        : m.mine
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-secondary-foreground"
                    }`}
                  >
                    {!m.deleted && (
                      <p
                        className={`mb-1 text-[10px] font-semibold uppercase tracking-wide ${
                          m.mine
                            ? "text-right text-primary-foreground/80"
                            : "text-left text-muted-foreground"
                        }`}
                      >
                        {m.mine ? "Du" : (data?.otherName ?? "Användare")}
                      </p>
                    )}
                    {m.deleted ? (
                      <p className="inline-flex items-center gap-1.5 italic">
                        <Ban className="h-3.5 w-3.5" />
                        {m.deletedByMe
                          ? "Du tog bort det här meddelandet"
                          : "Meddelandet togs bort av avsändaren"}
                      </p>
                    ) : (
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                    )}
                    <p
                      className={`mt-1 text-[11px] ${m.mine && !m.deleted ? "text-primary-foreground/80" : "text-muted-foreground"}`}
                    >
                      {formatDate(m.created_at)}
                    </p>
                  </div>
                  {!m.mine && !m.deleted && (
                    <button
                      type="button"
                      onClick={() => setPendingReport({ id: m.id, body: m.body, reported: m.reported })}
                      aria-label="Anmäl meddelandet"
                      title={
                        m.reported ? "Du har redan anmält det här meddelandet" : "Anmäl meddelandet"
                      }
                      className={`rounded-md p-1.5 transition-opacity hover:bg-secondary hover:text-destructive focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                        m.reported
                          ? "text-destructive opacity-100"
                          : "text-muted-foreground opacity-0 group-hover:opacity-100"
                      }`}
                    >
                      <Flag className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <form
        className="space-y-2 border-t border-border p-3 sm:p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim().length >= 2 && remaining > 0) send.mutate(body.trim());
        }}
      >
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          className="min-h-24 text-base"
          maxLength={2000}
          disabled={remaining <= 0}
          placeholder={
            remaining > 0 ? "Skriv ett svar…" : "Vänta på svar innan du skickar fler meddelanden."
          }
        />
        {remaining <= 0 ? (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" /> Du har skickat {data?.max ?? 5} meddelanden utan
            svar. Så fort motparten svarar kan du skriva igen – eller efter ett par dagar då du får
            skicka en påminnelse.
          </p>
        ) : (
          remaining <= 2 && (
            <p className="text-xs text-muted-foreground">
              {remaining} meddelande{remaining === 1 ? "" : "n"} kvar innan du behöver invänta svar.
            </p>
          )
        )}
        {send.isError && (
          <p className="text-sm text-destructive">
            {send.error instanceof Error ? send.error.message : "Kunde inte skicka svaret."}
          </p>
        )}
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
          <p className="flex min-w-0 items-start gap-1.5 text-xs text-foreground/75">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>Dela aldrig känsliga uppgifter i meddelanden.</span>
          </p>
          <Button
            type="submit"
            disabled={send.isPending || body.trim().length < 2 || remaining <= 0}
          >
            {send.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}{" "}
            Skicka
          </Button>
        </div>
      </form>

      <ReportMessage
        messageId={pendingReport?.id ?? ""}
        preview={pendingReport?.body ?? ""}
        reported={!!pendingReport?.reported}
        open={!!pendingReport}
        onOpenChange={(o) => !o && setPendingReport(null)}
        onReported={invalidate}
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort meddelandet?</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort det här meddelandet? Texten försvinner för både dig
              och mottagaren, och i stället visas att meddelandet togs bort. Det går inte att ångra.
              Innehållet raderas inte permanent utan sparas i vårt system, så att vi kan ta fram det
              vid till exempel en utredning eller anmälan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {remove.isError && (
            <p className="text-sm text-destructive">
              {remove.error instanceof Error
                ? remove.error.message
                : "Kunde inte ta bort meddelandet."}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (pendingDelete) remove.mutate(pendingDelete);
              }}
              disabled={remove.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {remove.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
